// modules/MaterialSystem.js
// 零件/材料系统模块
import DebugHelper from './DebugHelper.js';
import InventoryHelper from './InventoryHelper.js';
import { deepClone } from './utils/PlayerDataSync.js';

export default class MaterialSystem {
    /**
     * 材料类型定义
     */
    static MATERIAL_TYPES = {
        IRON: 'iron',
        COPPER: 'copper',
        STEEL: 'steel',
        GOLD: 'gold',
        DIAMOND: 'diamond'
    };
    
    /**
     * 装备分解材料表
     */
    static DISASSEMBLE_TABLE = {
        // 根据装备等级返回材料
        tier1: {
            iron: 5,
            copper: 2
        },
        tier2: {
            iron: 10,
            copper: 5,
            steel: 2
        },
        tier3: {
            iron: 20,
            copper: 10,
            steel: 5,
            gold: 2
        },
        tier4: {
            iron: 40,
            copper: 20,
            steel: 10,
            gold: 5,
            diamond: 1
        }
    };
    
    /**
     * 装备合成材料表
     */
    static CRAFT_TABLE = {
        // 合成装备所需的材料（通常是分解的2倍）
        tier1: {
            iron: 10,
            copper: 4
        },
        tier2: {
            iron: 20,
            copper: 10,
            steel: 4
        },
        tier3: {
            iron: 40,
            copper: 20,
            steel: 10,
            gold: 4
        },
        tier4: {
            iron: 80,
            copper: 40,
            steel: 20,
            gold: 10,
            diamond: 2
        }
    };
    
    /**
     * 获取玩家拥有的材料
     * @param {Object} player - 玩家对象
     * @returns {Object} 材料统计 {iron: number, copper: number, ...}
     */
    static getPlayerMaterials(player) {
        if (!player || !player.inventory) {
            return {};
        }
        
        const materials = {};
        
        player.inventory.forEach(item => {
            if (item.type === 'material') {
                const materialId = item.id || item.name;
                if (!materials[materialId]) {
                    materials[materialId] = 0;
                }
                materials[materialId]++;
            }
        });
        
        return materials;
    }
    
    /**
     * 获取材料数量
     * @param {Object} player - 玩家对象
     * @param {string} materialId - 材料ID
     * @returns {number} 材料数量
     */
    static getMaterialCount(player, materialId) {
        if (!player || !player.inventory) {
            return 0;
        }
        
        return player.inventory.filter(
            item => (item.id === materialId || item.name === materialId) && 
                    item.type === 'material'
        ).length;
    }
    
    /**
     * 添加材料到背包
     * @param {Object} player - 玩家对象
     * @param {string} materialId - 材料ID
     * @param {number} count - 数量
     */
    static addMaterial(player, materialId, count = 1) {
        if (!player.inventory) {
            player.inventory = [];
        }
        
        const materialData = this.getMaterialData(materialId);
        
        for (let i = 0; i < count; i++) {
            player.inventory.push({
                id: materialId,
                name: materialData.name,
                type: 'material',
                description: materialData.description
            });
        }
    }
    
    /**
     * 消耗材料
     * @param {Object} player - 玩家对象
     * @param {string} materialId - 材料ID
     * @param {number} count - 数量
     * @returns {boolean} 是否成功消耗
     */
    static consumeMaterial(player, materialId, count) {
        if (!player || !player.inventory) {
            return false;
        }
        
        const currentCount = this.getMaterialCount(player, materialId);
        if (currentCount < count) {
            return false;
        }
        
        let consumed = 0;
        player.inventory = player.inventory.filter(item => {
            if (consumed < count && 
                (item.id === materialId || item.name === materialId) && 
                item.type === 'material') {
                consumed++;
                return false; // 移除
            }
            return true; // 保留
        });
        
        return consumed === count;
    }
    
    /**
     * 分解装备
     * @param {Object} player - 玩家对象
     * @param {Object} equipment - 装备对象
     * @returns {Object} 分解结果 {success: boolean, materials: Object, message: string}
     */
    static disassembleEquipment(player, equipment) {
        if (!player || !equipment) {
            return {
                success: false,
                materials: {},
                message: '参数无效'
            };
        }
        
        // 检查装备是否在背包中
        const equipmentIndex = player.inventory.findIndex(
            item => item.id === equipment.id || item.name === equipment.name
        );
        
        if (equipmentIndex === -1) {
            return {
                success: false,
                materials: {},
                message: '装备不在背包中'
            };
        }
        
        // 获取装备等级
        const tier = equipment.tier || 1;
        const tierKey = `tier${tier}`;
        
        // 获取分解材料表
        const materials = this.DISASSEMBLE_TABLE[tierKey] || this.DISASSEMBLE_TABLE.tier1;
        
        // 移除装备
        player.inventory.splice(equipmentIndex, 1);
        
        // 添加材料
        const addedMaterials = {};
        for (const [materialId, count] of Object.entries(materials)) {
            this.addMaterial(player, materialId, count);
            addedMaterials[materialId] = count;
        }
        
        DebugHelper.log(`MaterialSystem: 分解 ${equipment.name}，获得材料:`, addedMaterials);
        
        return {
            success: true,
            materials: addedMaterials,
            message: `成功分解 ${equipment.name}，获得材料！`
        };
    }
    
    /**
     * 检查是否可以合成装备
     * @param {Object} player - 玩家对象
     * @param {Object} equipmentTemplate - 装备模板
     * @returns {Object} 检查结果 {canCraft: boolean, missing: Object, message: string}
     */
    static canCraftEquipment(player, equipmentTemplate) {
        if (!player || !equipmentTemplate) {
            return {
                canCraft: false,
                missing: {},
                message: '参数无效'
            };
        }
        
        const tier = equipmentTemplate.tier || 1;
        const tierKey = `tier${tier}`;
        const requiredMaterials = this.CRAFT_TABLE[tierKey] || this.CRAFT_TABLE.tier1;
        
        const missing = {};
        let allEnough = true;
        
        for (const [materialId, requiredCount] of Object.entries(requiredMaterials)) {
            const currentCount = this.getMaterialCount(player, materialId);
            if (currentCount < requiredCount) {
                missing[materialId] = requiredCount - currentCount;
                allEnough = false;
            }
        }
        
        if (allEnough) {
            return {
                canCraft: true,
                missing: {},
                message: '可以合成'
            };
        } else {
            const missingList = Object.entries(missing).map(
                ([id, count]) => `${this.getMaterialData(id).name} x${count}`
            ).join(', ');
            return {
                canCraft: false,
                missing: missing,
                message: `材料不足：缺少 ${missingList}`
            };
        }
    }
    
    /**
     * 合成装备
     * @param {Object} player - 玩家对象
     * @param {Object} equipmentTemplate - 装备模板
     * @returns {Object} 合成结果 {success: boolean, equipment: Object, message: string}
     */
    static craftEquipment(player, equipmentTemplate) {
        const check = this.canCraftEquipment(player, equipmentTemplate);
        
        if (!check.canCraft) {
            return {
                success: false,
                equipment: null,
                message: check.message
            };
        }
        
        const tier = equipmentTemplate.tier || 1;
        const tierKey = `tier${tier}`;
        const requiredMaterials = this.CRAFT_TABLE[tierKey] || this.CRAFT_TABLE.tier1;
        
        // 消耗材料
        for (const [materialId, count] of Object.entries(requiredMaterials)) {
            if (!this.consumeMaterial(player, materialId, count)) {
                return {
                    success: false,
                    equipment: null,
                    message: `材料消耗失败：${materialId}`
                };
            }
        }
        
        // 创建装备
        const equipment = deepClone(equipmentTemplate);
        
        // 添加到背包
        if (!player.inventory) {
            player.inventory = [];
        }
        player.inventory.push(equipment);
        
        DebugHelper.log(`MaterialSystem: 成功合成 ${equipment.name}`);
        
        return {
            success: true,
            equipment: equipment,
            message: `成功合成 ${equipment.name}！`
        };
    }
    
    /**
     * 获取材料数据
     * @param {string} materialId - 材料ID
     * @returns {Object} 材料数据
     */
    static getMaterialData(materialId) {
        const materialData = {
            iron: { name: '铁', description: '基础材料，用于合成和强化装备' },
            copper: { name: '铜', description: '常见材料，用于合成装备' },
            steel: { name: '钢', description: '高级材料，用于合成高级装备' },
            gold: { name: '金', description: '稀有材料，用于合成稀有装备' },
            diamond: { name: '钻石', description: '传说材料，用于合成传说装备' }
        };
        
        return materialData[materialId] || { name: materialId, description: '未知材料' };
    }
}

